import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { LayoutDashboard, Shield, Users, LogOut, FileText, User, Settings, HelpCircle, Globe } from 'lucide-react';
import { toast } from './ui/sonner';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [userInfo, setUserInfo] = useState({ name: '', employee_id: '', username: '' });
  const { t, isRtl, language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserRole(response.data.role);
        setUserInfo({
          name: response.data.name || response.data.username,
          employee_id: response.data.employee_id || '',
          username: response.data.username
        });
      } catch (error) {
        console.error('Failed to fetch user info');
      }
    };
    fetchUserInfo();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    toast.success(language === 'en' ? 'Logged out successfully' : 'تم تسجيل الخروج بنجاح');
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', labelKey: 'dashboard', icon: LayoutDashboard, adminOnly: false },
    { path: '/permissions', labelKey: 'permissions', icon: Shield, adminOnly: false },
    { path: '/users', labelKey: 'users', icon: Users, adminOnly: true },
    { path: '/audit-logs', labelKey: 'auditLogs', icon: FileText, adminOnly: true },
    { path: '/settings', labelKey: 'settings', icon: Settings, adminOnly: false },
    { path: '/help', labelKey: 'helpManual', icon: HelpCircle, adminOnly: false },
  ];

  return (
    <div className={`flex min-h-screen bg-slate-50 ${isRtl ? 'flex-row-reverse' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <aside className={`sidebar w-64 p-6 flex flex-col ${isRtl ? 'rounded-r-none rounded-l-xl' : ''}`}>
        <div className={`mb-6 ${isRtl ? 'text-right' : ''}`}>
          <h1 className="text-2xl font-bold text-white">TankGuard</h1>
          <p className="text-slate-400 text-sm mt-1">Enterprise</p>
        </div>

        {/* Language Toggle */}
        <button
          onClick={toggleLanguage}
          className={`mb-4 flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 transition-all ${isRtl ? 'flex-row-reverse' : ''}`}
        >
          <Globe className="w-4 h-4 text-blue-400" />
          <span className="text-white text-sm font-medium">
            {language === 'en' ? 'العربية' : 'English'}
          </span>
        </button>

        {/* User Info Section */}
        <div className="mb-6 p-3 bg-white/10 rounded-lg border border-white/20">
          <div className={`flex items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className="bg-blue-500 p-2 rounded-full">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className={`flex-1 min-w-0 ${isRtl ? 'text-right' : ''}`}>
              <p className="text-white font-medium text-sm truncate">{userInfo.name}</p>
              {userInfo.employee_id && (
                <p className="text-slate-400 text-xs">{isRtl ? `${userInfo.employee_id} :ID` : `ID: ${userInfo.employee_id}`}</p>
              )}
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-2">
          {navItems.map((item) => {
            // Hide admin-only items for non-admin users
            if (item.adminOnly && userRole !== 'admin') {
              return null;
            }
            
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.labelKey}`}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isRtl ? 'flex-row-reverse' : ''} ${
                  isActive
                    ? 'bg-white/10 text-white border border-white/20'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{t[item.labelKey]}</span>
              </button>
            );
          })}
        </nav>

        <Button
          data-testid="logout-button"
          onClick={handleLogout}
          variant="outline"
          className={`w-full border-white/20 text-white hover:bg-white/10 hover:text-white ${isRtl ? 'flex-row-reverse' : ''}`}
        >
          <LogOut className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
          {t.logout}
        </Button>
      </aside>

      <main className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
